import { useContext, useRef } from 'react';
import { makeStyles } from '@mui/styles';

import { translate } from '@openclassrooms/translations';
import PageLoading from '@classify/react-core-components/PageLoading';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import browser from '@openclassrooms/utils/browser';
import { MetaGlobal } from '@openclassrooms/utils/page-header';

import Callout from '@classify/react-core-components/Callout';
import Typography from '@classify/react-core-components/Typography';

import CourseOverview from '../components/CourseOverview';
import CourseTabBar from '../components/CourseTabBar';
import TableOfContent from '../components/TableOfContent';
import CourseContext from '../contexts/courseContext';
import Hero from '../components/Hero';
import CourseCredits from '../components/CourseCredits';
import CourseFooter from '../components/CourseFooter';
import PathPromotion from '../components/PathPromotion';
import { TocVariants } from '../components/TableOfContent/TableOfContent';
import CourseEditionPanel from '../components/CourseEditionPanel';
import EditionPanelVariants from '../constants/EditionPanelVariants';
import SinglePageCourseContent from '../components/SinglePageCourseContent';
import COURSE_TABS from '../constants/courseTabs';
import getCourseJsonLD from '../services/getCourseJsonLD';

const useStyles = makeStyles(
  ({ spacing, palette }) => ({
    title: {
      borderBottom: `1px solid ${palette.line}`,
      margin: spacing(5, 0, 3, 0),
      paddingBottom: spacing(1),
    },
    editionPanel: {
      marginTop: spacing(5),
    },
    overview: {
      marginBottom: spacing(9),
    },
  }),
  { classNamePrefix: 'CourseHomeScreen' }
);

const LMS_REFERRERS = ['360learning'];

const CourseHomeScreen = () => {
  const classes = useStyles();
  const { course, courseId, currentUser, canEditCourse, userToc } = useContext(CourseContext);
  const footerRef = useRef(null);

  if (!course) {
    return <PageLoading pastDelay />;
  }

  const { referrer } = browser.document;
  const isLmsReferrer =
    referrer && LMS_REFERRERS.find((lmsReferrer) => referrer.includes(lmsReferrer));
  const shouldDisplaySwitchAccountWarning =
    isLmsReferrer && currentUser && !currentUser.organization;

  const {
    title,
    shortDescription,
    illustration,
    authors,
    creatorPartners,
    license,
    updatedAt,
    categoryColor,
    isActive,
    isPreregistrable,
    isSinglePage,
  } = course;

  const { TAB_OVERVIEW, TAB_TOC, TAB_CREDITS } = COURSE_TABS;

  const jsonLD = getCourseJsonLD(course, userToc);

  return (
    <>
      <MetaGlobal
        title={title}
        description={shortDescription}
        imageUrl={illustration}
        translations={{
          twitterAccount: translate('global.twitter_account'),
        }}
        jsonLD={jsonLD}
      />

      {shouldDisplaySwitchAccountWarning && (
        <ContentWrapper>
          <Callout type="warning" sx={{ marginY: 2 }}>
            <Typography variant="h5">
              {translate('course_home.coming_from_LMS.wrong_account.banner.title')}
            </Typography>
            <Typography variant="body">
              {translate('course_home.coming_from_LMS.wrong_account.banner.desc')}
            </Typography>
          </Callout>
        </ContentWrapper>
      )}

      <Hero />

      {canEditCourse && (
        <ContentWrapper className={classes.editionPanel}>
          <CourseEditionPanel
            courseId={courseId}
            isPublished={Boolean(course.publishedAt)}
            variant={EditionPanelVariants.HOMEPAGE}
          />
        </ContentWrapper>
      )}

      {!isSinglePage && <CourseTabBar footerRef={footerRef} />}

      <div className={classes.overview} id={TAB_OVERVIEW}>
        {isSinglePage ? (
          <SinglePageCourseContent />
        ) : (
          <>
            <CourseOverview />
            {(isActive || isPreregistrable) && <PathPromotion />}
          </>
        )}
      </div>

      {!isSinglePage && (
        <ContentWrapper id={TAB_TOC}>
          <TableOfContent color={categoryColor} variant={TocVariants.HOMEPAGE} />
        </ContentWrapper>
      )}

      <div id={TAB_CREDITS}>
        <CourseCredits
          teachers={authors}
          creators={creatorPartners}
          license={license}
          updatedAt={updatedAt}
        />
      </div>

      {!isSinglePage && (
        <div ref={footerRef}>
          <CourseFooter />
        </div>
      )}
    </>
  );
};

export default CourseHomeScreen;
